import type React from 'react';
import type { CSSProperties, Ref } from 'react';
import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import SegmentedTab from 'components/partials/segmented-tab/segmented-tab';
import type { SegmentedTabOption } from 'components/partials/segmented-tab/segmented-tab';
import ActionsDropdown from 'components/partials/actions-dropdown/actions-dropdown';
import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';
import { ChartFrame } from 'components/partials/charts/shared/chart-frame';

import { useMediaQuery } from 'hooks/use-media-query';
import type { ChartExportFormat } from 'hooks/use-chart-export';

import { trackChartExported } from '../../../analytics/chart-analytics';

import { breakpoints } from 'src/design-tokens/primitives';

import Icons from 'assets/icons';

export type ChartViewMode = 'vertical' | 'horizontal' | 'table';

interface ChartPartFrameProps {
  title?: string;
  subtitle?: string;
  viewMode: ChartViewMode;
  onViewModeChange: (viewMode: ChartViewMode) => void;
  /** Width (px) the plot would prefer. Drives card lower bound. */
  preferredContentWidthPx?: number;
  forceMaxWidth?: boolean;
  chartAreaRef?: Ref<HTMLDivElement>;
  onExport?: (format: ChartExportFormat) => Promise<boolean>;
  isExporting?: boolean;
  children: React.ReactNode;
}

const VIEW_MODE_ICON_CLASS = 'size-5';
const VIEW_MODE_OPTIONS: readonly SegmentedTabOption<ChartViewMode>[] = [
  {
    value: 'vertical',
    label: 'Vertical bar chart view',
    icon: <Icons.BarChartAscending customClass={VIEW_MODE_ICON_CLASS} />,
  },
  {
    value: 'horizontal',
    label: 'Horizontal bar chart view',
    icon: <Icons.BarChartHorizontal customClass={VIEW_MODE_ICON_CLASS} />,
  },
  {
    value: 'table',
    label: 'Table view',
    icon: <Icons.Table customClass={VIEW_MODE_ICON_CLASS} />,
  },
];
const LG_SCREEN_QUERY = `(min-width: ${breakpoints.lg}px)`;

const EXPORT_OPTIONS: ReadonlyArray<{
  format: ChartExportFormat;
  label: string;
  icon: React.ReactNode;
}> = [
  { format: 'png', label: 'Download PNG', icon: <Icons.Camera /> },
  { format: 'csv', label: 'Download CSV', icon: <Icons.Columns /> },
];

const ChartPartFrame = ({
  title,
  subtitle,
  viewMode,
  onViewModeChange,
  preferredContentWidthPx,
  forceMaxWidth = false,
  chartAreaRef,
  onExport,
  isExporting = false,
  children,
}: ChartPartFrameProps): React.JSX.Element => {
  const isLgScreen = useMediaQuery(LG_SCREEN_QUERY);
  const exportMenuOptions: ActionsDropdownOption[] = onExport
    ? EXPORT_OPTIONS.map(({ format, label, icon }) => ({
        label,
        startIcon: icon,
        isDisabled: isExporting,
        onClick: async () => {
          const isSuccessful = await onExport(format);

          trackChartExported({
            format,
            viewMode,
            chartTitle: title,
            status: isSuccessful ? 'successful' : 'errored',
          });
        },
      }))
    : [];
  const shouldForceFullWidth = isLgScreen && forceMaxWidth;

  const frameStyle: CSSProperties | undefined =
    preferredContentWidthPx || shouldForceFullWidth
      ? {
          ...(preferredContentWidthPx && {
            minWidth: `min(100%, ${preferredContentWidthPx}px)`,
          }),
          ...(shouldForceFullWidth && { width: '100%' }),
        }
      : undefined;

  return (
    <ChartFrame
      chartAreaRef={chartAreaRef}
      title={title}
      subtitle={subtitle}
      className="w-fit max-w-full min-w-0 my-2"
      style={frameStyle}
      settingsBar={
        <Stack
          data-export-ignore="true"
          role="group"
          aria-label="Chart settings"
          direction="row"
          sx={{
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <SegmentedTab
            label="Chart display mode"
            options={VIEW_MODE_OPTIONS}
            value={viewMode}
            onChange={onViewModeChange}
          />
          {!!exportMenuOptions.length && (
            <ActionsDropdown
              options={exportMenuOptions}
              menuPosition="right"
              loading={isExporting}
              isDisabled={isExporting}
              startIcon={<Icons.Download customClass="!size-4" />}
              ariaLabel="Download chart"
              customClass="size-[34px] p-0 border-solid"
            />
          )}
        </Stack>
      }
    >
      <Box sx={{ minWidth: 0, width: '100%' }}>{children}</Box>
    </ChartFrame>
  );
};

export default ChartPartFrame;
